import React from "react"
import { useSearchSelector } from "./search-redux-context"
import useSearchContext, { SearchContextValue } from "./use-search-context"
import { Bucket } from "./store/facets-slice"
import { filterValueUpdaters } from "./use-filter"

type FilterUpdater = (prev: string[]) => string[]
const DEREGISTERED_FACET = {
  isRegistered: false,
} as const

// Re-exported from use-filter for backward compatibility
export const filterUpdaters = filterValueUpdaters

export type RegisteredUseFacetReturn = {
  isRegistered: true
  buckets: Bucket[]
  filters: string[]
  setFilters(updater: FilterUpdater): void
  toggleFilter(name: string): void
  addFilter(name: string): void
  removeFilter(name: string): void
  clearFilters(): void
  search(): void
}

export type DeregisteredUseFacetReturn = {
  isRegistered: false
}
export type UseFacetReturn =
  | RegisteredUseFacetReturn
  | DeregisteredUseFacetReturn
export default function useFacet(
  facetId: string,
  searchMethods?: SearchContextValue,
): UseFacetReturn {
  const {
    filters: { valuesSelector, updateValues },
    facets: { registeredSelector, bucketsSelector },
    search,
  } = useSearchContext(searchMethods)
  const isRegistered = useSearchSelector(registeredSelector(facetId))
  const buckets = useSearchSelector(bucketsSelector(facetId))
  const filters = useSearchSelector(valuesSelector(facetId))
  const updateFilters = React.useCallback(
    (updater: FilterUpdater) => updateValues(facetId, updater),
    [facetId, updateValues],
  )
  const toggleFilter = React.useCallback(
    (name: string) => updateFilters(filterUpdaters.toggle(name)),
    [updateFilters],
  )
  const addFilter = React.useCallback(
    (name: string) => updateFilters(filterUpdaters.add(name)),
    [updateFilters],
  )
  const removeFilter = React.useCallback(
    (name: string) => updateFilters(filterUpdaters.remove(name)),
    [updateFilters],
  )
  const clearFilters = React.useCallback(
    () => updateFilters(filterUpdaters.clear),
    [updateFilters],
  )

  const registeredFacet = React.useMemo(
    () => ({
      isRegistered: true,
      buckets: buckets as Bucket[],
      filters: filters as string[],
      setFilters: updateFilters,
      toggleFilter,
      addFilter,
      removeFilter,
      clearFilters,
      search,
    }),
    [
      buckets,
      filters,
      updateFilters,
      toggleFilter,
      addFilter,
      removeFilter,
      clearFilters,
      search,
    ],
  )

  return isRegistered ? registeredFacet : DEREGISTERED_FACET
}
